import React, { useEffect, useLayoutEffect, useRef, useCallback, useState } from 'react'
import PropTypes from 'prop-types'
import styles from '../../assets/stylesheets/pages/searches/sidebar.module.scss'
import CheckFilter from './check_filter'
import SliderFilter from './slider_filter'
import SliderMinMaxFilter from './slider_min_max_filter'
import AtLeastSliderFilter from './at_least_slider_filter'
import FilterUtils from './filter_utils'

import { OffCanvas } from 'lensrentals-components'

const sortFilters = (filters = {}) => {
  const orderedFilters = {}
  const orderedKeys = Object.keys(filters).sort((a, b) => {
    const aOrder = filterOrderOverride(a)
    const bOrder = filterOrderOverride(b)
    if (aOrder < bOrder) {
      return -1
    }
    if (aOrder > bOrder) {
      return 1
    }

    if (filters[a].priority !== filters[b].priority) {
      return filters[a].priority - filters[b].priority
    }

    // simply sort on the name for filters with the same priority
    return a - b
  })

  orderedKeys.forEach(function(key) {
    orderedFilters[key] = filters[key]
  })

  return orderedFilters
}

const filterOrderOverride = (filterName) => {
  switch (filterName) {
    case 'Refine By':
      return 0
    case 'Rent or Buy':
      return 1
    case 'Recommended For':
      return 2
    case 'Brand':
      // Brand comes back with a "3" for priority, so force it to the top
      return 3
    default:
      return 4
  }
}

// eslint-disable-next-line camelcase
const format = ({ agg_number_facet = {}, agg_string_facet = {} }) => {
  const aggString = {}

  aggString['Refine By'] = {
    name: 'Refine By',
    type: 'check',
    filterType: 'string_filters',
    values: [{
      name: 'Hide out of stock'
    }]
  }

  const hasRentFilter = Object.keys(agg_string_facet).includes('For Rent')
  const hasSaleFilter = Object.keys(agg_string_facet).includes('For Sale')
  aggString['Rent or Buy'] = {
    name: 'Rent or Buy',
    type: 'check',
    filterType: 'string_filters',
    values: [
      { name: 'Rent', count: hasRentFilter ? agg_string_facet['For Rent'].document_count : 0 },
      { name: 'Buy', count: hasSaleFilter ? agg_string_facet['For Sale'].document_count : 0 }
    ]
  }

  Object.entries(agg_string_facet).forEach(entry => {
    const [key, value] = entry
    if (key !== 'Category Paths' && key != 'For Rent' && key !== 'For Sale') {
      aggString[key] = {
        name: key,
        type: 'check',
        filterType: 'string_filters',
        priority: value.spec_priority,
        values: Object.entries(value.facet_values).map(entry => {
          const [key, value] = entry
          return {
            name: key,
            count: value
          }
        })
      }
    }
  })

  const aggNumber = {}
  Object.entries(agg_number_facet).forEach(entry => {
    let type = 'slider'
    let filterType = 'number_filters'
    const [key, value] = entry

    if (key === 'Max Aperture') {
      type = 'at-least-slider'
      filterType = 'number_range_filters'
    }

    aggNumber[key] = {
      name: key,
      type: type,
      filterType: filterType,
      priority: value.spec_priority,
      values: Object.entries(value.facet_values).map(entry => {
        const [key] = entry
        return parseFloat(key)
      }).sort((a, b) => a - b)
    }
  })

  const aggMinMax = {}
  // loop through numbers again and combine any min max pairs
  Object.entries(aggNumber).forEach(entry => {
    const [key, value] = entry

    if (key.includes('(min)') || key.includes('(max)')) {
      // get the name without (min)/(max)
      const name = key.replace(' (min)', '').replace(' (max)', '')

      // get the value inside of the parens
      const regex = /\(([^)]+)\)/
      const type = regex.exec(key)[1]

      // create the new key if it doesn't exist
      if (!aggMinMax[name]) {
        aggMinMax[name] = {
          name: name,
          type: 'min-max',
          filterType: 'number_range_filters'
        }
      }

      // populate the values with min or max
      aggMinMax[name].values = { ...aggMinMax[name].values, [type]: value.values }

      // remove original key from aggNumber
      delete aggNumber[key]
    }
  })

  const allFilters = sortFilters({ ...aggString, ...aggNumber, ...aggMinMax })
  return allFilters
}

const Sidebar = ({ filters, data, selectedFilters, handleFilterSelection }) => {
  // this fixes a stacking context issue with the sidebar and the header at small screen sizes
  useLayoutEffect(() => {
    const siteHeader = document.getElementsByClassName('navbar-default header')[0]
    if (getComputedStyle(siteHeader).position === 'sticky') {
      document.querySelector(':root').style.setProperty('--stickyHeaderHeight', `${siteHeader.clientHeight}px`)
    }
  })

  // this is all logic to make sure filters don't shift around when the user is selecting
  const containerRef = useRef(null)
  // the element whose vertical location should stay the same
  const filterGroupRef = useRef(null)
  // store the scroll position between renders
  const previousScrollPositionRef = useRef(null)

  const handleFilterSelectionWithPosition = useCallback((newSelectedFilters, event) => {
    // measure BEFORE React re-renders
    filterGroupRef.current = event?.target.closest('details')
    // offsetTop relative to the container
    // because of the <OffCanvas/> we can't apply a ref so it needs to be found via the filter we CAN track
    const sidebar = filterGroupRef.current.closest('#sidebar')
    // find the visual scroll position of the filter group
    previousScrollPositionRef.current = filterGroupRef.current.offsetTop - sidebar.scrollTop

    // run the actual filter selection
    handleFilterSelection(newSelectedFilters)
  }, [handleFilterSelection])

  // after react re-renders, if the previousScrollPositionRef then adjust
  useLayoutEffect(() => {
    // don't run this before a user clicks anything
    // containerRef can be null if we're on mobile
    if (!filterGroupRef.current || previousScrollPositionRef.current === null || containerRef.current === null) return

    // we have to query both of these again because it may have been re-rendered away
    // CSS.escape() escapes characters (eg. `&`) that are invalid in a CSS identifier, so ids containing them don't break the selector
    const clickedFilterGroup = containerRef.current.querySelector(`#${CSS.escape(filterGroupRef.current.id)}`)
    const sidebar = clickedFilterGroup?.closest('#sidebar')

    // calculate what the new scroll should be
    const newScrollPosition = clickedFilterGroup.offsetTop - previousScrollPositionRef.current

    // try to scroll the sidebar that much
    sidebar.scrollTop = newScrollPosition
  })
  //

  useEffect(() => {
    // this is to make the session product type change effect the filters when the user is on a search page
    const toggle = document.getElementById('show-buy-links')
    const forSaleStringFilter = { name: 'For Sale', values: ['Yes'] }
    const forRentStringFilter = { name: 'For Rent', values: ['Yes'] }

    const onChange = event => {
      const filters = JSON.parse(selectedFilters)

      if (event.target.checked) { // the user is toggling to 'buy'
        // only do this if the filter isn't already set
        filters.string_filters = filters.string_filters.filter(filter => JSON.stringify(filter) !== JSON.stringify(forRentStringFilter))
        if (!JSON.stringify(filters.string_filters).includes(JSON.stringify(forSaleStringFilter))) {
          filters.string_filters.push(forSaleStringFilter)
        }
      } else { // the user is toggling to 'rent'
        filters.string_filters = filters.string_filters.filter(filter => JSON.stringify(filter) !== JSON.stringify(forSaleStringFilter))
        if (!JSON.stringify(filters.string_filters).includes(JSON.stringify(forRentStringFilter))) {
          filters.string_filters.push(forRentStringFilter)
        }
      }

      handleFilterSelection(JSON.stringify(filters))
    }

    toggle?.addEventListener('change', onChange)

    // remove this listener when the component is unmounted
    return () => toggle.removeEventListener('change', onChange)
  })

  const [openFilters, setOpenFilters] = useState({})
  const toggleOpenFilters = filter => {
    setOpenFilters(opened => ({
      ...opened,
      [filter]: !opened[filter]
    }))
  }
  const [expandedFilters, setExpandedFilters] = useState({})
  const toggleExpandedFilters = filter => {
    setExpandedFilters(expanded => ({
      ...expanded,
      [filter]: !expanded[filter]
    }))
  }

  let currentFilters = filters
  if (data && data.filters && JSON.stringify(data.filters) !== JSON.stringify(filters)) {
    currentFilters = data.filters
  }
  let formattedFilters = {}
  if (currentFilters) {
    formattedFilters = format(currentFilters)
  }

  return (
    <OffCanvas
      anchor='right'
      breakpoint={768}
      button={{
        icon: 'Filter',
        color: 'confirm'
      }}
      className={styles.sidebar}
    >
      <OffCanvas.Title>
        Filters:
      </OffCanvas.Title>
      <OffCanvas.Content>
        <aside ref={containerRef}>
          {Object.values(formattedFilters).map((filter, index) => {
            // filters default to being open but not expanded, these checks reflect that
            const isOpen = !openFilters[filter.name] // a user has to explicitly close it
            const isExpanded = !!expandedFilters[filter.name] // a user has to explicitly expand it

            switch (filter.type) {
              case 'check':
                return (
                  <CheckFilter
                    key={index}
                    selectFilter={FilterUtils().selectFilter}
                    deselectFilter={FilterUtils().deselectFilter}
                    selectedFilters={selectedFilters}
                    handleFilterSelection={handleFilterSelectionWithPosition}
                    isOpen={isOpen}
                    toggleOpenFilters={toggleOpenFilters}
                    isExpanded={isExpanded}
                    toggleExpandedFilters={toggleExpandedFilters}
                    {...filter}
                  />
                )
              case 'slider':
                return (
                  <SliderFilter
                    key={index}
                    selectFilter={FilterUtils().selectFilter}
                    selectedFilters={selectedFilters}
                    handleFilterSelection={handleFilterSelectionWithPosition}
                    isOpen={isOpen}
                    toggleOpenFilters={toggleOpenFilters}
                    isExpanded={isExpanded}
                    toggleExpandedFilters={toggleExpandedFilters}
                    {...filter}
                  />
                )
              case 'at-least-slider':
                return (
                  <AtLeastSliderFilter
                    key={index}
                    selectAtLeastFilter={FilterUtils().selectAtLeastFilter}
                    selectedFilters={selectedFilters}
                    handleFilterSelection={handleFilterSelectionWithPosition}
                    isOpen={isOpen}
                    toggleOpenFilters={toggleOpenFilters}
                    isExpanded={isExpanded}
                    toggleExpandedFilters={toggleExpandedFilters}
                    {...filter}
                  />
                )
              case 'min-max':
                return (
                  <SliderMinMaxFilter
                    key={index} selectMinMaxFilter={FilterUtils().selectMinMaxFilter}
                    selectedFilters={selectedFilters}
                    handleFilterSelection={handleFilterSelectionWithPosition}
                    isOpen={isOpen}
                    toggleOpenFilters={toggleOpenFilters}
                    isExpanded={isExpanded}
                    toggleExpandedFilters={toggleExpandedFilters}
                    {...filter}
                  />
                )
              default:
                return null
            }
          })}
        </aside>
      </OffCanvas.Content>
    </OffCanvas>
  )
}
Sidebar.propTypes = {
  filters: PropTypes.object.isRequired,
  data: PropTypes.object,
  selectedFilters: PropTypes.string.isRequired,
  handleFilterSelection: PropTypes.func.isRequired
}
export default Sidebar
